import React, { useEffect, useRef, useState } from "react"
import { Button, Card, Col, ProgressBar, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import API from "../../common/API"

type ImportStage =
  | "Queued"
  | "Truncating"
  | "BulkLoading"
  | "BulkLoadingStaging"
  | "CreatingStagingIndex"
  | "MergingByPeriod"
  | "DroppingStaging"
  | "UpdatingStatistics"
  | "Completed"
  | "Failed"
  | "Cancelled"

type ImportMode = "full" | "complementary"

interface ImportStatus {
  jobId: string
  fileName: string
  fileSizeMB: number
  stage: ImportStage
  message?: string
  errorMessage?: string
  rowsProcessed: number
  megabytesProcessed: number
  progressPercent: number
  currentLine: number
  rowsPerSecond: number
  megabytesPerSecond: number
  skippedRows: number
  elapsedSeconds: number
  etaSeconds?: number | null
  startedAt: string
  finishedAt?: string | null
  // Complementary-specific
  currentPeriod?: string | null
  periodsProcessed?: number
  periodsTotal?: number
  rowsInserted?: number
  rowsSkippedDuplicate?: number
}

interface DryRunResponse {
  fileName: string
  fileSizeMB: number
  linesScanned: number
  sampleRows: Record<string, string>[]
  anomalies: {
    wrongColumnCount: number
    invalidCedula: number
    invalidFechaSalario: number
    invalidDecimalSalarioISR: number
  }
  distinctFechaSalarioInSample: string[]
}

const formatSeconds = (s?: number | null) => {
  if (s == null || !isFinite(s)) return "—"
  const sec = Math.max(0, Math.round(s))
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const r = sec % 60
  if (h > 0) return `${h}h ${m}m ${r}s`
  if (m > 0) return `${m}m ${r}s`
  return `${r}s`
}

const stageVariant = (stage: ImportStage) => {
  switch (stage) {
    case "Completed":
      return "success"
    case "Failed":
      return "danger"
    case "Cancelled":
      return "warning"
    case "BulkLoading":
    case "BulkLoadingStaging":
    case "CreatingStagingIndex":
    case "MergingByPeriod":
    case "DroppingStaging":
    case "UpdatingStatistics":
      return "info"
    default:
      return "secondary"
  }
}

const isTerminal = (stage: ImportStage) =>
  stage === "Completed" || stage === "Failed" || stage === "Cancelled"

const AdminSalariosComponent: React.FC = () => {
  const { t } = useTranslation("global")
  const [fileName, setFileName] = useState("personas.csv")
  const [batchSize, setBatchSize] = useState(500000)
  const [startFromLine, setStartFromLine] = useState(0)
  const [isStarting, setIsStarting] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isDryRunning, setIsDryRunning] = useState(false)
  const [status, setStatus] = useState<ImportStatus | null>(null)
  const [mode, setMode] = useState<ImportMode>("full")
  const [dryRun, setDryRun] = useState<DryRunResponse | null>(null)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Complementary import state
  const [complFileName, setComplFileName] = useState("Salarios20250917.txt")
  const [complSeparator, setComplSeparator] = useState<"tab" | "comma" | "semicolon" | "pipe">("tab")
  const [complHasHeader, setComplHasHeader] = useState(true)
  const [isStartingCompl, setIsStartingCompl] = useState(false)

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  const pollOnce = async (jobId: string) => {
    try {
      const res = await API.getAction(`Salario/ImportStatus/${jobId}`)
      if (res.data.status === "ok") {
        const s = res.data.response as ImportStatus
        setStatus(s)
        if (isTerminal(s.stage)) {
          stopPolling()
          if (s.stage === "Completed") toast.success(t("Importación completada"))
          else if (s.stage === "Failed") toast.error(t("La importación falló"))
          else if (s.stage === "Cancelled") toast.warning(t("Importación cancelada"))
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const startPolling = (jobId: string) => {
    stopPolling()
    pollOnce(jobId)
    pollTimerRef.current = setInterval(() => pollOnce(jobId), 3000)
  }

  useEffect(() => {
    return () => stopPolling()
  }, [])

  const handleDryRun = async () => {
    try {
      setIsDryRunning(true)
      setDryRun(null)
      const res = await API.getAction(
        `Salario/ImportSalariosDryRun?fileName=${encodeURIComponent(fileName)}&rows=10000`
      )
      if (res.data.status === "ok") {
        setDryRun(res.data.response as DryRunResponse)
        toast.success(t("Validación de archivo completada"))
      } else {
        toast.error(res.data.response ?? t("Error en validación"))
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error en validación"))
    } finally {
      setIsDryRunning(false)
    }
  }

  const handleStart = async () => {
    if (
      !window.confirm(
        String(
          t(
            "Esto importará el archivo completo en la tabla Salario. Confirma que ya ejecutaste la Fase 1 del runbook SQL."
          )
        )
      )
    )
      return

    try {
      setIsStarting(true)
      const params =
        `fileName=${encodeURIComponent(fileName)}` +
        `&separator=comma&hasHeader=false&batchSize=${batchSize}` +
        `&startFromLine=${startFromLine}`
      const res = await API.postAction(`Salario/ImportSalariosFull?${params}`)
      if (res.data.status === "ok") {
        const jobId = res.data.response.jobId as string
        toast.success(t("Importación iniciada"))
        const initialStatus: ImportStatus = {
          jobId,
          fileName,
          fileSizeMB: res.data.response.fileSizeMB,
          stage: "BulkLoading",
          rowsProcessed: 0,
          megabytesProcessed: 0,
          progressPercent: 0,
          currentLine: 0,
          rowsPerSecond: 0,
          megabytesPerSecond: 0,
          elapsedSeconds: 0,
          startedAt: res.data.response.startedAt,
          skippedRows: 0,
        }
        setStatus(initialStatus)
        startPolling(jobId)
      } else {
        toast.error(res.data.response ?? t("Error al iniciar"))
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error al iniciar"))
    } finally {
      setIsStarting(false)
    }
  }

  const handleStartComplementary = async () => {
    if (
      !window.confirm(
        String(
          t(
            "Esto importará el archivo complementario deduplicando por (EmpleadorID, IdEmpleado, FechaSalario). Confirma que ya ejecutaste el rebuild del índice con INCLUDE (EmpleadorId)."
          )
        )
      )
    )
      return

    try {
      setIsStartingCompl(true)
      setMode("complementary")
      const params =
        `fileName=${encodeURIComponent(complFileName)}` +
        `&separator=${complSeparator}` +
        `&hasHeader=${complHasHeader}` +
        `&batchSize=${batchSize}`
      const res = await API.postAction(`Salario/ImportSalariosComplementary?${params}`)
      if (res.data.status === "ok") {
        const jobId = res.data.response.jobId as string
        toast.success(t("Importación complementaria iniciada"))
        const initialStatus: ImportStatus = {
          jobId,
          fileName: complFileName,
          fileSizeMB: res.data.response.fileSizeMB,
          stage: "BulkLoadingStaging",
          rowsProcessed: 0,
          megabytesProcessed: 0,
          progressPercent: 0,
          currentLine: 0,
          rowsPerSecond: 0,
          megabytesPerSecond: 0,
          elapsedSeconds: 0,
          startedAt: res.data.response.startedAt,
          skippedRows: 0,
          periodsProcessed: 0,
          periodsTotal: 0,
          rowsInserted: 0,
          rowsSkippedDuplicate: 0,
        }
        setStatus(initialStatus)
        startPolling(jobId)
      } else {
        toast.error(res.data.response ?? t("Error al iniciar"))
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.response?.data?.response ?? e.message ?? t("Error al iniciar"))
    } finally {
      setIsStartingCompl(false)
    }
  }

  const handleCancel = async () => {
    if (!status?.jobId) return
    if (!window.confirm(String(t("¿Cancelar la importación en curso?")))) return
    try {
      setIsCancelling(true)
      const res = await API.postAction(`Salario/ImportCancel/${status.jobId}`)
      if (res.data.status === "ok") {
        toast.info(t("Cancelación solicitada"))
      }
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message ?? t("Error al cancelar"))
    } finally {
      setIsCancelling(false)
    }
  }

  const isRunning = status != null && !isTerminal(status.stage)

  return (
    <div className="my-4">
      <h4 className="mb-3">
        <i className="fas fa-coins me-2" />
        {t("Importar Salarios")}
      </h4>

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("1. Configuración")}</strong>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <label className="form-label">{t("Nombre del archivo")}</label>
              <input
                type="text"
                className="form-control"
                value={fileName}
                onChange={e => setFileName(e.target.value)}
                disabled={isRunning}
              />
              <small className="text-muted">
                {t("Debe estar en wwwroot/supportDocuments del API")}
              </small>
            </Col>
            <Col md={3}>
              <label className="form-label">{t("Tamaño de batch")}</label>
              <input
                type="number"
                className="form-control"
                value={batchSize}
                onChange={e => setBatchSize(Number(e.target.value) || 500000)}
                min={50000}
                max={2000000}
                step={50000}
                disabled={isRunning}
              />
            </Col>
            <Col md={3}>
              <label className="form-label">{t("Reanudar desde línea")}</label>
              <input
                type="number"
                className="form-control"
                value={startFromLine}
                onChange={e => setStartFromLine(Math.max(0, Number(e.target.value) || 0))}
                min={0}
                step={100000}
                disabled={isRunning}
              />
              <small className="text-muted">{t("0 = inicio del archivo")}</small>
            </Col>
            <Col md={3}>
              <label className="form-label">&nbsp;</label>
              <Button
                variant="outline-secondary"
                className="d-block w-100"
                onClick={handleDryRun}
                disabled={isDryRunning || isRunning}
              >
                {isDryRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t("Validando...")}
                  </>
                ) : (
                  <>
                    <i className="fas fa-vial me-2" />
                    {t("Validar archivo (Dry Run)")}
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {dryRun && (
        <Card className="mb-3">
          <Card.Header className="bg-light">
            <strong>{t("2. Resultado de validación")}</strong>
          </Card.Header>
          <Card.Body>
            <Row className="mb-3">
              <Col md={3}>
                <small className="text-muted d-block">{t("Tamaño")}</small>
                <strong>{dryRun.fileSizeMB.toLocaleString()} MB</strong>
              </Col>
              <Col md={3}>
                <small className="text-muted d-block">{t("Filas escaneadas")}</small>
                <strong>{dryRun.linesScanned.toLocaleString()}</strong>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">
                  {t("Períodos en muestra")}
                </small>
                <strong>{dryRun.distinctFechaSalarioInSample.join(", ") || "—"}</strong>
              </Col>
            </Row>

            <h6>{t("Anomalías")}</h6>
            <Table size="sm" bordered className="mb-3">
              <tbody>
                <tr>
                  <td>{t("Filas con columnas ≠ 11")}</td>
                  <td className="text-end">
                    <span
                      className={
                        dryRun.anomalies.wrongColumnCount > 0 ? "text-danger" : "text-success"
                      }
                    >
                      {dryRun.anomalies.wrongColumnCount.toLocaleString()}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>{t("Cédulas inválidas")}</td>
                  <td className="text-end">
                    {dryRun.anomalies.invalidCedula.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>{t("Fechas inválidas")}</td>
                  <td className="text-end">
                    {dryRun.anomalies.invalidFechaSalario.toLocaleString()}
                  </td>
                </tr>
                <tr>
                  <td>{t("Decimales inválidos en SalarioISR")}</td>
                  <td className="text-end">
                    {dryRun.anomalies.invalidDecimalSalarioISR.toLocaleString()}
                  </td>
                </tr>
              </tbody>
            </Table>

            <h6>{t("Primeras filas")}</h6>
            <div style={{ overflowX: "auto" }}>
              <Table size="sm" bordered striped className="small mb-0">
                <thead>
                  <tr>
                    {dryRun.sampleRows[0] &&
                      Object.keys(dryRun.sampleRows[0]).map(k => <th key={k}>{k}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {dryRun.sampleRows.map((r, idx) => (
                    <tr key={idx}>
                      {Object.values(r).map((v, i) => (
                        <td key={i}>{v ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="mb-3 border-info">
        <Card.Header className="bg-info bg-opacity-10 d-flex justify-content-between align-items-center">
          <strong>
            <i className="fas fa-layer-group me-2" />
            {t("Importación complementaria (agrega solo filas nuevas)")}
          </strong>
          <small className="text-muted">
            {t("Dedup por EmpleadorID + IdEmpleado + FechaSalario")}
          </small>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 mb-3">
            <Col md={5}>
              <label className="form-label">{t("Archivo complementario")}</label>
              <input
                type="text"
                className="form-control"
                value={complFileName}
                onChange={e => setComplFileName(e.target.value)}
                disabled={isRunning}
              />
            </Col>
            <Col md={3}>
              <label className="form-label">{t("Separador")}</label>
              <select
                className="form-control"
                value={complSeparator}
                onChange={e => setComplSeparator(e.target.value as any)}
                disabled={isRunning}
              >
                <option value="tab">Tab</option>
                <option value="comma">Coma</option>
                <option value="semicolon">Punto y coma</option>
                <option value="pipe">Pipe</option>
              </select>
            </Col>
            <Col md={2}>
              <label className="form-label">{t("Tiene header")}</label>
              <select
                className="form-control"
                value={complHasHeader ? "yes" : "no"}
                onChange={e => setComplHasHeader(e.target.value === "yes")}
                disabled={isRunning}
              >
                <option value="yes">{t("Sí")}</option>
                <option value="no">{t("No")}</option>
              </select>
            </Col>
            <Col md={2}>
              <label className="form-label">&nbsp;</label>
              <Button
                variant="info"
                className="d-block w-100 text-white"
                onClick={handleStartComplementary}
                disabled={isStartingCompl || isRunning}
              >
                {isStartingCompl ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t("Iniciando...")}
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus-circle me-2" />
                    {t("Importar complementario")}
                  </>
                )}
              </Button>
            </Col>
          </Row>
          <div className="alert alert-info mb-0 small">
            <i className="fas fa-info-circle me-2" />
            {t(
              "Pasos: 1) crea tabla staging, 2) bulk load del archivo, 3) crea índice de staging, 4) INSERT por período solo de filas no existentes, 5) drop staging. Espacio temporal pico ~90 GB en E:."
            )}
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header className="bg-light">
          <strong>{t("3. Importación")}</strong>
        </Card.Header>
        <Card.Body>
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2" />
            {t(
              "Antes de iniciar: ejecuta la Fase 1 del runbook SQL (recovery SIMPLE, drop index, TRUNCATE). Después de Completed, ejecuta la Fase 3 (recrear índice + UPDATE STATISTICS)."
            )}
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={handleStart}
              disabled={isStarting || isRunning}
            >
              {isStarting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  {t("Iniciando...")}
                </>
              ) : (
                <>
                  <i className="fas fa-play me-2" />
                  {t("Iniciar importación")}
                </>
              )}
            </Button>

            {isRunning && (
              <Button
                variant="outline-danger"
                size="lg"
                onClick={handleCancel}
                disabled={isCancelling}
              >
                <i className="fas fa-stop me-2" />
                {isCancelling ? t("Cancelando...") : t("Cancelar")}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {status && (
        <Card>
          <Card.Header className="bg-light d-flex justify-content-between align-items-center">
            <strong>{t("4. Progreso")}</strong>
            <span className={`badge bg-${stageVariant(status.stage)}`}>{status.stage}</span>
          </Card.Header>
          <Card.Body>
            <ProgressBar
              now={status.progressPercent}
              label={`${status.progressPercent.toFixed(1)}%`}
              variant={stageVariant(status.stage)}
              animated={isRunning}
              striped={isRunning}
              style={{ height: 28 }}
              className="mb-3"
            />

            <Row className="g-3">
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Filas procesadas")}</small>
                <strong>{status.rowsProcessed.toLocaleString()}</strong>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("MB procesados")}</small>
                <strong>
                  {status.megabytesProcessed.toLocaleString()} / {status.fileSizeMB.toLocaleString()}
                </strong>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Velocidad")}</small>
                <strong>
                  {status.rowsPerSecond.toLocaleString()} {t("filas/s")}
                </strong>
                <br />
                <small>{status.megabytesPerSecond.toLocaleString()} MB/s</small>
              </Col>
              <Col md={3} sm={6}>
                <small className="text-muted d-block">{t("Tiempo")}</small>
                <strong>{formatSeconds(status.elapsedSeconds)}</strong>
                <br />
                <small>
                  ETA: {isRunning ? formatSeconds(status.etaSeconds) : "—"}
                </small>
              </Col>
            </Row>

            {(status.periodsTotal ?? 0) > 0 && (
              <div className="mt-3 p-3 bg-light rounded">
                <h6 className="mb-2">
                  <i className="fas fa-calendar-alt me-2" />
                  {t("Progreso complementario (por período)")}
                </h6>
                <ProgressBar
                  now={
                    status.periodsTotal
                      ? ((status.periodsProcessed ?? 0) * 100) / status.periodsTotal
                      : 0
                  }
                  label={`${status.periodsProcessed ?? 0} / ${status.periodsTotal ?? 0}`}
                  variant="info"
                  className="mb-3"
                />
                <Row className="g-3">
                  <Col md={3} sm={6}>
                    <small className="text-muted d-block">{t("Período actual")}</small>
                    <strong>{status.currentPeriod ?? "—"}</strong>
                  </Col>
                  <Col md={3} sm={6}>
                    <small className="text-muted d-block">{t("Filas insertadas")}</small>
                    <strong className="text-success">
                      {(status.rowsInserted ?? 0).toLocaleString()}
                    </strong>
                  </Col>
                  <Col md={3} sm={6}>
                    <small className="text-muted d-block">{t("Duplicadas omitidas")}</small>
                    <strong className="text-muted">
                      {(status.rowsSkippedDuplicate ?? 0).toLocaleString()}
                    </strong>
                  </Col>
                  <Col md={3} sm={6}>
                    <small className="text-muted d-block">{t("Períodos totales")}</small>
                    <strong>{status.periodsTotal ?? 0}</strong>
                  </Col>
                </Row>
              </div>
            )}

            {(status.skippedRows ?? 0) > 0 && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fas fa-exclamation-triangle me-2" />
                {t("Filas omitidas por datos corruptos")}:{" "}
                <strong>{status.skippedRows.toLocaleString()}</strong>
              </div>
            )}

            {status.errorMessage && (
              <div className="alert alert-danger mt-3 mb-0">
                <strong>{t("Error")}:</strong> {status.errorMessage}
              </div>
            )}
            {status.message && status.stage === "Completed" && (
              <div className="alert alert-success mt-3 mb-0">
                <i className="fas fa-check-circle me-2" />
                {status.message}{" "}
                <strong>
                  {t("Recuerda ejecutar la Fase 3 del runbook SQL (CREATE INDEX + UPDATE STATISTICS).")}
                </strong>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default AdminSalariosComponent
